import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { SubTask, SubTaskGroup, SubTaskPrompt, SubTaskRole, SubTaskStudent } from "../../interfaces/Task";
import SubTaskGroupItem from "./SubTaskGroupItem";
import SubTaskRoleItem from "./SubTaskRoleItem";
import SubTaskPromptItem from "./SubTaskPromptItem";
import SubTaskPromptEdit from "./SubTaskPromptEdit";
import Help from "../../elements/HelpContent";
import SubTaskRoleItemAssignment from "./SubTaskRoleItemAssignment";
import { Media } from "../../interfaces/Media";
import CustomEditor from "../../elements/CustomEditor";
import { registerEventEnd, registerEventStart } from "../../../utils/utils";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";

const emptySubTask = {
    _id: '',
    name: '',
    updated: new Date(),
    groups: [],
    roles: [],
    prompts: [],
    goal: '',
    students: 0,
    grouping: '',
    studentsPerGroup: 0,
    authorship: '',
    session: '',
    students_session: false,
    teachers_session: false,
    teacher_notes: ''
}

export interface EditSubTaskProps {
    taskId: string;
    task_language?: string;
    //subtaskId: string;
    subTask: SubTask;
    saveCallback: CallableFunction;
    closeCallback: CallableFunction;
}

const emptySubTaskPrompt = {
    _id: '',
    name: '',
    instructions: '',
    media: [],
    text: '',
    formatted_text: '',
    roles: [],
    teacher_notes: ''
}

const emptySubTaskRole = {
    _id: '',
    name: '',
    color: '',
    groups: []
}

export default function EditSubTask(props: EditSubTaskProps) {
    const { t } = useTranslation();
    
    const [eventId, setEventId] = useState('');
    const [showEditPrompt, setShowEditPrompt] = useState(false);
    const [showEditTeacherNotes, setShowEditTeacherNotes] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState<SubTaskPrompt>(emptySubTaskPrompt);
    const [selectedPromptPosition, setSelectedPromptPosition] = useState(0);
    const [showEditRoleAssignment, setShowEditRoleAssignment] = useState(false);
    const [selectedRole, setSelectedRole] = useState<SubTaskRole>(emptySubTaskRole);
    const [selectedRolePosition, setSelectedRolePosition] = useState(0);
    const [showTaskProperties, setShowTaskProperties] = useState(false);

    const handleShowEditPrompt = (p: SubTaskPrompt, position: number) => {
        setSelectedPrompt(p);
        setSelectedPromptPosition(position);
        setShowEditPrompt(true)
    }

    const handleShowEditRoleAssignment = (r: SubTaskRole, position: number) => {
        setSelectedRole(r);
        setSelectedRolePosition(position);
        setShowEditRoleAssignment(true)
    }

    const toggleShowTaskProperties = () => {
        setShowTaskProperties(!showTaskProperties);
        
    }

    const handleChangeProperty = (e: any) => {
        const newSubTask = Object.assign({}, props.subTask);
        switch (e.target.name) {
            case 'goal':
                newSubTask.goal = e.target.value;
                break;
            case 'students':
                newSubTask.students = e.target.value;
                break;
            case 'grouping':
                newSubTask.grouping = e.target.value;
                break;
            case 'groupingsize':
                newSubTask.studentsPerGroup = e.target.value;
                break;
        }
        // setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleSavePrompt = (prompt: SubTaskPrompt, position: number) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.prompts = props.subTask.prompts.map((p: SubTaskPrompt, index) => {
            if (index == position) {
                const newPrompt = Object.assign({}, p);
                newPrompt.instructions = prompt.instructions;
                newPrompt.media = prompt.media.map((m: Media) => {
                    return m;
                });
                newPrompt.name = prompt.name;
                newPrompt.text = prompt.text;
                newPrompt.formatted_text = prompt.formatted_text;
                newPrompt.teacher_notes = prompt.teacher_notes;
                newPrompt.roles = prompt.roles;
                return (newPrompt);
            } else {
                return p;
            }
        });
        // setSubTask(newSubTask);
        setShowEditPrompt(false);
        props.saveCallback(newSubTask);
    }

    const handleSaveRoleAssignment = (role: SubTaskRole, position: number) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.roles = props.subTask.roles.map((r: SubTaskRole, index) => {
            if (index == position) {
                const newRole = Object.assign({}, r);
                newRole.groups = role.groups;
                return (newRole);
            } else {
                return r;
            }
        });
        // setSubTask(newSubTask);
        setShowEditRoleAssignment(false);
        props.saveCallback(newSubTask);
    }

    const handleDeletePrompt = (prompt: SubTaskPrompt, position: number) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.groups = props.subTask.groups.map((g: SubTaskGroup) => {
            const newGroup = Object.assign({}, g);
            newGroup.students = g.students.map((s: SubTaskStudent) => {
                const newStudent = Object.assign({}, s);
                if (newStudent.prompt == position) {
                    newStudent.prompt = -1;
                }
                return newStudent;
            })
            return newGroup;
        });
        newSubTask.prompts = props.subTask.prompts.filter((p: SubTaskPrompt, index) => index != position);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleHideEditPrompt = () => {
        setShowEditPrompt(false)
    }

    const handleHideEditRoleAssignment = () => {
        setShowEditRoleAssignment(false)
    }
    
    const handleAddNewGroup = () => {
        let newSubTask = Object.assign({}, props.subTask);
        let newGroup:SubTaskGroup = {
            _id: '',
            name: t("Group") + ' ' + (props.subTask.groups.length + 1),
            color: '',
            students: []
        }
        newSubTask.groups.push(newGroup);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleDeleteGroup = (group: SubTaskGroup) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.groups = props.subTask.groups.filter((g: SubTaskGroup) => g._id != group._id);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleCloneGroup = (group: SubTaskGroup) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.groups = props.subTask.groups.map((g: SubTaskGroup) => { return g; });
        newSubTask.groups.push(group)
        props.saveCallback(newSubTask);
    }

    const handleAddNewRole = () => {
        let newSubTask = Object.assign({}, props.subTask);
        let newRole:SubTaskRole = {
            _id: '',
            name: 'R' + (props.subTask.roles.length + 1),
            color: '',
            groups: []
        }
        newSubTask.roles.push(newRole);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleSaveRole = (role: SubTaskRole, name: string, color: string, position: number) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.roles = props.subTask.roles.map((r: SubTaskRole, index) => {
            if (index == position) {
                const updatedRole = Object.assign({}, r);
                updatedRole.name = name;
                updatedRole.color = color;
                return updatedRole;
            } else {
                return r;
            }
        });
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleDeleteRole = (role: SubTaskRole) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.roles = props.subTask.roles.filter((r: SubTaskRole) => r._id != role._id);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleAddNewPrompt = () => {
        let newSubTask = Object.assign({}, props.subTask);
        let newPrompt:SubTaskPrompt = {
            _id: '',
            name: t("Prompt") + ' ' + (props.subTask.prompts.length + 1),
            instructions: '',
            media: [],
            text: '',
            formatted_text: '',
            roles: [],
            teacher_notes: ''
        }
        newSubTask.prompts.push(newPrompt);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleClonePrompt = (p: SubTaskPrompt) => {
        let newSubTask = Object.assign({}, props.subTask);
        let newPrompt:SubTaskPrompt = {
            _id: '',
            name: p.name + ' ' + t("(copy)"),
            instructions: p.instructions,
            media: p.media.map((m: Media) => { return m }),
            text: p.text,
            formatted_text: p.formatted_text,
            roles: p.roles,
            teacher_notes: p.teacher_notes
        }
        newSubTask.prompts.push(newPrompt);
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleClose = () => {
        registerEventEnd(eventId);
        props.closeCallback();
    }

    const handleSaveSubTask = (e: any) => {
        e.preventDefault();
        props.closeCallback();
    }

    const saveGroup = (group: SubTaskGroup, name: string, color: string, students: SubTaskStudent[], position: number) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.groups = props.subTask.groups.map((g: SubTaskGroup, index) => {
            if (index == position) {
                const updatedGroup = Object.assign({}, g);
                updatedGroup.name = name;
                updatedGroup.color = color;
                updatedGroup.students = students;
                return updatedGroup;
            } else {
                return g;
            }
        });

        // Update roles to reflect which groups they belong to
        newSubTask.roles = newSubTask.roles.map((role: SubTaskRole, roleIndex: number) => {
            const updatedRole = Object.assign({}, role);
            const groupsForThisRole: number[] = [];

            // Check all groups to see if this role is used by any student
            newSubTask.groups.forEach((g: SubTaskGroup, groupIndex: number) => {
                const roleUsedInGroup = g.students.some((s: SubTaskStudent) => s.role === roleIndex);
                if (roleUsedInGroup && !groupsForThisRole.includes(groupIndex)) {
                    groupsForThisRole.push(groupIndex);
                }
            });

            updatedRole.groups = groupsForThisRole;
            return updatedRole;
        });

        // setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    const handleNameChange = (e: any) => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.name = e.target.value;
        //setSubTask(newSubTask);
        props.saveCallback(newSubTask);
    }

    useEffect(() => {
        registerEventStart('edit subtask', props.taskId).then(data => {
            setEventId(data);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (eventId) {
                registerEventEnd(eventId);
            }
        };
    }, [eventId]);

    const nullSubmit = (e: any) => {
        e.preventDefault();
    }

    const [teacherNotes, setTeacherNotes] = useState(props.subTask.teacher_notes || '');

    const handleToggleTeacherNotes = (event: any) => {
        event.preventDefault()
        if (!showEditTeacherNotes) {
            // Opening - sync local state with props
            setTeacherNotes(props.subTask.teacher_notes || '');
        }
        setShowEditTeacherNotes(!showEditTeacherNotes)
    }

    const handleTeacherNotesChange = (data: string) => {
        setTeacherNotes(data);
    }

    const handleSaveTeacherNotes = () => {
        const newSubTask = Object.assign({}, props.subTask);
        newSubTask.teacher_notes = teacherNotes;
        props.saveCallback(newSubTask);
        setShowEditTeacherNotes(false);
    }

    const doNothing = (text: string) => {
        // placeholder for formatted text callback
    }

    // Sync local state when props change
    useEffect(() => {
        setTeacherNotes(props.subTask.teacher_notes || '');
    }, [props.subTask.teacher_notes]);

    return(
        <>
            { !showEditPrompt &&
                <Overlay title={ t("Edit Main Task") } onClose={ handleClose } className="edit-sub-task-container">
                    <div className="inner">
                        <Form className="edit-sub-task-form" onSubmit={ handleSaveSubTask }>
                                    <Form.Group className="field field-title">
                                        <Form.Label>{ 'Main Task title' }</Form.Label>
                                        <Form.Control type="text" name="name" required
                                            value={ props.subTask.name } onChange={ handleNameChange } />
                                        {/*
                                        <div className={ 'task-properties' + (showTaskProperties ? ' show' : '')}>
                                            <div className="inner">
                                                <a className="toggle-properties" onClick={ toggleShowTaskProperties }>{ 'Configure your task' }</a>
                                                <div className="task-properties-form" onSubmit={ nullSubmit }>
                                                    <div className="form-field goal">
                                                        <FormLabel htmlFor="goal">{ 'The specific goal of this task phase is to…' }</FormLabel>
                                                        <FormControl as="textarea" value={ props.subTask.goal } name="goal" onChange={ handleChangeProperty } rows={ 10 } />
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}
                                    </Form.Group>
                                    <div className={ 'field field-teacher-notes' + (showEditTeacherNotes ? ' expanded' : '') }>
                                        <a onClick={ handleToggleTeacherNotes } className={ 'teacher-notes-toggle' + (showEditTeacherNotes ? ' expanded' : '') + (props.subTask.teacher_notes ? ' has-content' : '') }>
                                            { t("Teacher notes") }
                                        </a>
                                        { showEditTeacherNotes &&
                                            <div className="teacher-notes-editor">
                                                <CustomEditor
                                                    change={ handleTeacherNotesChange }
                                                    changeFormatted={ doNothing }
                                                    data={ teacherNotes }
                                                    formatted={ '' }
                                                    id="subtask-teachernotes"
                                                    label={ t("Teacher notes") }
                                                    npl={ false }
                                                    task_id={ props.taskId }
                                                />
                                                <div className="teacher-notes-actions">
                                                    <button type="button" className="btn btn-primary btn-sm" onClick={ handleSaveTeacherNotes }>
                                                        { t("Save") }
                                                    </button>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    <div className="sub-task-components">
                                        <div className="left">
                                            <div className="groups">
                                                { props.subTask.groups.map((g: SubTaskGroup, index) => {
                                                    return <SubTaskGroupItem group={ g } key={ 'group-' + g._id } position={ index }
                                                        saveCallback={ saveGroup } deleteCallback={ handleDeleteGroup } cloneCallback={ handleCloneGroup }
                                                        roles={ props.subTask.roles } prompts={ props.subTask.prompts } />
                                                })}
                                                <div className="add-group">
                                                    <a title={ t("Add new group") } onClick={ handleAddNewGroup }>
                                                        { t("Add new group") }
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="right">
                                            <div className="roles">
                                                <h4>{ t("Roles") }<Help id={ '650b0d67b0f9abea0305888f' } /></h4>
                                                <a className="add-new" title={ t("Add new role") } onClick={ handleAddNewRole }>
                                                    { t("Add new role") }
                                                </a>
                                                <div className="roles-list">
                                                    { props.subTask.roles.map((r: SubTaskRole, index) => {
                                                        return (<SubTaskRoleItem role={ r }
                                                            editCallback={ handleShowEditRoleAssignment }
                                                            saveCallback={ handleSaveRole }
                                                            deleteCallback={ handleDeleteRole }
                                                            assignmentsCallback={ handleShowEditRoleAssignment }
                                                            position={ index }
                                                            key={ 'role-' + r._id } />)
                                                    })}
                                                </div>
                                            </div>
                                            <div className="prompts">
                                                <h4>{ t("Prompts") }<Help id={ '650b1253b0f9abea030588b6' } /></h4>
                                                <a className="add-new" title={ t("Add new prompt") } onClick={ handleAddNewPrompt }>
                                                    { t("Add new prompt") }
                                                </a>
                                                <div className="prompts-list">
                                                    { props.subTask.prompts.map((p: SubTaskPrompt, index) => {
                                                            return (<SubTaskPromptItem prompt={ p } key={ 'group-' + p._id }
                                                                editPrompt={ handleShowEditPrompt }
                                                                deletePrompt={ handleDeletePrompt }
                                                                clonePrompt={ handleClonePrompt }
                                                                position={ index } />)
                                                        })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="actions">
                                        <button className="btn btn-primary" type="submit" onClick={ handleSaveSubTask }>
                                            { t("Save") }
                                        </button>
                                    </div>
                                </Form>
                    </div>
                </Overlay>
            }
            { showEditPrompt &&
                <Overlay title={t("Edit Prompt")} onClose={handleHideEditPrompt}>
                    <SubTaskPromptEdit task_id={ props.taskId } task_language={ props.task_language } subTask={ props.subTask } prompt={ selectedPrompt } position={ selectedPromptPosition } save={ handleSavePrompt } />
                </Overlay>
            }
            { showEditRoleAssignment &&
                <Overlay title={t("Edit Role Assignment")} onClose={handleHideEditRoleAssignment}>
                    <SubTaskRoleItemAssignment subTask={ props.subTask } role={ selectedRole }
                        position={ selectedRolePosition } saveCallback={ handleSaveRoleAssignment } />
                </Overlay>
            }
        </>
    );
}